// LICENSE_CODE ZON
'use strict'; /*jslint react:true*/
import React, {useState, useCallback, useMemo} from 'react';
import cn from 'classnames';
import {useStore} from '@nanostores/react';
import {$is_root} from '@pub/store/user.js';
import {fmt_money} from '@pub/utils/misc.js';
import {IS_ANDROID, IS_IOS} from '@pub/utils/env.js';
import {OFFER_REWARD_STATUS} from '@pub/utils/constants.js';
import {Money} from '@pub/comp/money';
import {Text} from '@pub/comp/text';
import {Icon} from '@pub/comp/icon';
import {Tooltip} from '@pub/comp/tooltip';
import {Button} from '@pub/comp/button';
import {Qr_code} from '@pub/comp/qr_code';
import {use_footer_content, use_header_content} from './hooks.js';
import {Status_badge} from './status_badge.js';
import {Cover} from './cover.js';
import {Platform_list} from './platform_list.js';
import {get_platforms} from './util.js';
import * as cns from './details.module.less';

function Header({offer}){
    return <div className={cns.img_container}>
      <Cover className={cns.img} data={offer}/>
      <div className={cns.img_info}>
        <Status_badge status={offer.status}/>
        <Text.xxl mono bold>{fmt_money(offer.payout)}</Text.xxl>
      </div>
    </div>;
}

function Rewards({list, is_root}){
    return <div>
      <Text.l>Rewards</Text.l>
      <ul className={cns.reward_list}>
        {list.map(it=>{
            const is_done = it.status==OFFER_REWARD_STATUS.COMPLETED;
            return <li key={it.id}
              className={cn(cns.reward_item, is_done&&cns.reward_item__done)}>
              <Text.m className={cns.reward_payout} mono accent>
                {fmt_money(it.payout)}
              </Text.m>
              <div>
                <Text.m secondary>{it.name}</Text.m>
                {is_root&&<Text.xs tertiary mono>ID: {it.id}</Text.xs>}
              </div>
              {is_done&&<Tooltip tip="Done" placement="left">
                <div className={cns.reward_check}>
                  <Icon ico="check" size="s" color="accent"/>
                </div>
              </Tooltip>}
            </li>;
        })}
      </ul>
    </div>;
}

function Steps({offer}){
    if (!offer.instructions)
        return null;
    return <div>
      <Text.l>Steps</Text.l>
      <Text.m secondary>
        <p dangerouslySetInnerHTML={{__html: offer.instructions}}/>
      </Text.m>
    </div>;
}

function Body({offer}){
    const is_root = useStore($is_root);
    return <div className={cns.body}>
      <div className={cns.info}>
        <Text.l>Description</Text.l>
        <Text.m tertiary>{offer.description}</Text.m>
      </div>
      {offer.rewards?.length>0 && <Rewards list={offer.rewards}
        is_root={is_root}/>}
      <Steps offer={offer}/>
      {is_root&&<div className={cns.info}>
        <Text.s mono>Root meta</Text.s>
        <Text.xs secondary mono>Network: {offer.network}</Text.xs>
        <Text.xs secondary mono>ID: {offer.id}</Text.xs>
      </div>}
    </div>;
}

function Footer({offer, on_close, on_ok}){
    const {
        has_all,
        has_android,
        has_ios,
        has_desktop,
    } = get_platforms(offer);
    const MAYBY_DESKTOP = !IS_IOS&&!IS_ANDROID;
    const instant_open = has_all
        || has_ios&&IS_IOS
        || has_android&&IS_ANDROID
        || has_desktop&&MAYBY_DESKTOP;
    const handle_click = useCallback(()=>{
        if (instant_open)
            on_close();
        else
            on_ok();
    }, [offer, on_close, on_ok, instant_open]);
    const content = useMemo(()=>{
        return <div className={cns.footer}>
          <Button ico="play" on_click={handle_click} color_scheme="primary"
            filled size="l" href={instant_open ? offer.url : undefined}
            in_new_tab hide_new_tab_ico bold>
            Play and Earn{' '}{fmt_money(offer.payout)}
          </Button>
        </div>;
    }, [handle_click, offer.payout]);
    use_footer_content(content);
    return null;
}

function Info_screen({offer, on_close, on_ok}){
    return <>
      <Header offer={offer} on_close={on_close}/>
      <Body offer={offer}/>
      <Footer offer={offer} on_close={on_close} on_ok={on_ok}/>
    </>;
}

function QR_offer({offer}){
    return <div className={cns.qr_offer}>
      <img className={cns.qr_offer_img} src={offer.icon}/>
      <div>
        <Text.s>{offer.name}</Text.s>
        <Text.s tertiary>{offer.description}</Text.s>
      </div>
      <Money className={cns.qr_payout} size="s">{offer.payout}</Money>
    </div>;
}

function Qr_screen({offer, on_back}){
    const header_content = useMemo(()=>{
        return <Button on_click={on_back} ico="arrow_up" tip="Back"
          rotate_ico={270} color_scheme="tertiary" filled round
          className={cns.back_btn}/>;
    }, [on_back]);
    use_header_content(header_content);
    return <div className={cns.qr_container}>
      <Text.xxl bold>Scan the QR code with your phone</Text.xxl>
      <Qr_code className={cns.qr} url={offer.url}/>
      <Text.xl>Available devices</Text.xl>
      <Platform_list className={cns.qr_devices} offer={offer}/>
      <QR_offer offer={offer}/>
    </div>;
}

export function Details({on_close, offer}){
    const [show_qr, set_show_qr] = useState(false);
    const toggle_qr = useCallback(()=>set_show_qr(v=>!v), []);
    if (!offer)
        return null;
    return <div className={cns.root}>
      {show_qr
        ? <Qr_screen offer={offer} on_close={on_close} on_back={toggle_qr}/>
        : <Info_screen offer={offer} on_close={on_close} on_ok={toggle_qr}/>
      }
    </div>;
}
